import { useEffect, useId, useState } from 'react';
import { Check, ClipboardPaste, Copy, Pipette, RotateCcw } from 'lucide-react';
import { normalizeHexColor } from '@peles/shared';

/**
 * Any colour, however it is had: the browser's picker, typed or pasted as hex,
 * copied out to use elsewhere, picked off the screen with the eyedropper
 * (where the browser has one — a colour straight from a photo on screen), or
 * one press on a suggested swatch.
 *
 * `value` undefined means "not chosen here": the field shows `inheritedValue`
 * — what is in force without a choice — and says where it comes from.
 */

interface EyeDropperResult {
  sRGBHex: string;
}
interface EyeDropperLike {
  open(): Promise<EyeDropperResult>;
}
type EyeDropperCtor = new () => EyeDropperLike;

function eyeDropper(): EyeDropperCtor | null {
  const ctor = (globalThis as { EyeDropper?: EyeDropperCtor }).EyeDropper;
  return typeof ctor === 'function' ? ctor : null;
}

export interface ColorSwatch {
  color: string;
  label?: string;
}

export interface ColorFieldProps {
  label: string;
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  /** What shows when nothing is chosen here. */
  inheritedValue?: string | null;
  /** Where that comes from: "From your pictures", "The event's". */
  inheritedLabel?: string;
  /** One-press choices — the pictures' colours, named presets. */
  swatches?: ColorSwatch[];
  /** Offer "Reset" back to the inherited colour. Defaults to true. */
  clearable?: boolean;
  disabled?: boolean;
}

const FULL_HEX = /^#?[0-9a-f]{6}$/i;

const BUTTON =
  'inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-9 sm:min-w-9 rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50';

export default function ColorField({
  label,
  value,
  onChange,
  inheritedValue,
  inheritedLabel,
  swatches = [],
  clearable = true,
  disabled = false,
}: ColorFieldProps) {
  const id = useId();
  const shown = value ?? inheritedValue ?? null;
  const [draft, setDraft] = useState(shown ?? '');
  const [note, setNote] = useState<string | null>(null);

  // Whether the text was typed into since it last showed the colour in force:
  // leaving the field untouched must not turn an inherited colour into a choice.
  const [edited, setEdited] = useState(false);

  // A change from outside (a swatch, the AI, a reset) shows in the text field.
  useEffect(() => {
    setDraft(shown ?? '');
    setEdited(false);
  }, [shown]);

  useEffect(() => {
    if (!note) return;
    const timer = setTimeout(() => setNote(null), 2000);
    return () => clearTimeout(timer);
  }, [note]);

  const apply = (raw: string) => {
    const hex = normalizeHexColor(raw);
    if (hex) onChange(hex);
    return hex;
  };

  /** Takes what was typed, short form included, or puts the colour in force back. */
  const settle = () => {
    const hex = edited ? apply(draft) : null;
    setDraft(hex ?? shown ?? '');
    setEdited(false);
  };

  const copy = async () => {
    if (!shown) return;
    try {
      await navigator.clipboard.writeText(shown);
      setNote('Copied');
    } catch {
      setNote("Couldn't copy");
    }
  };

  const paste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setNote(apply(text) ? 'Pasted' : 'No colour on the clipboard');
    } catch {
      setNote("Couldn't read the clipboard");
    }
  };

  const Dropper = eyeDropper();
  const pick = async () => {
    if (!Dropper) return;
    try {
      const { sRGBHex } = await new Dropper().open();
      apply(sRGBHex);
    } catch {
      // Escape closes the eyedropper: nothing chosen, nothing to say.
    }
  };

  const draftValid = draft === '' || normalizeHexColor(draft) !== null;

  return (
    <div className="space-y-1.5">
      <label htmlFor={`${id}-hex`} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <div className="flex flex-wrap items-center gap-1.5">
        <input
          type="color"
          aria-label={`${label}: pick a colour`}
          value={shown ?? '#ffffff'}
          disabled={disabled}
          onChange={(e) => apply(e.target.value)}
          className="h-11 w-14 sm:h-9 shrink-0 cursor-pointer rounded-md border border-gray-300 bg-white p-1"
        />
        <input
          id={`${id}-hex`}
          type="text"
          value={draft}
          disabled={disabled}
          placeholder="#rrggbb"
          spellCheck={false}
          aria-invalid={!draftValid}
          onChange={(e) => {
            setDraft(e.target.value);
            setEdited(true);
            // Only a whole #rrggbb applies as it is typed: #123 is also a
            // colour, and taking it would rewrite the field under the cursor
            // before the other three digits arrive. A short form applies on
            // leaving the field or Enter.
            if (FULL_HEX.test(e.target.value.trim())) apply(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              settle();
            }
          }}
          onBlur={settle}
          className={`w-28 min-h-11 sm:min-h-9 rounded-md border px-2 font-mono text-sm ${
            draftValid ? 'border-gray-300' : 'border-red-400'
          }`}
        />
        <button
          type="button"
          className={BUTTON}
          onClick={() => void copy()}
          disabled={disabled || !shown}
          aria-label={`Copy ${label.toLowerCase()} colour`}
          title="Copy colour"
        >
          <Copy className="h-4 w-4" />
        </button>
        <button
          type="button"
          className={BUTTON}
          onClick={() => void paste()}
          disabled={disabled}
          aria-label={`Paste a colour into ${label.toLowerCase()}`}
          title="Paste colour"
        >
          <ClipboardPaste className="h-4 w-4" />
        </button>
        {Dropper && (
          <button
            type="button"
            className={BUTTON}
            onClick={() => void pick()}
            disabled={disabled}
            aria-label={`Pick ${label.toLowerCase()} from the screen`}
            title="Pick a colour from the screen"
          >
            <Pipette className="h-4 w-4" />
          </button>
        )}
        {clearable && value !== undefined && (
          <button
            type="button"
            className={`${BUTTON} gap-1 px-2 text-xs`}
            onClick={() => onChange(undefined)}
            disabled={disabled}
            aria-label={`Reset ${label.toLowerCase()}`}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>
        )}
        {note && (
          <span role="status" className="inline-flex items-center gap-1 text-xs text-gray-500">
            <Check className="h-3.5 w-3.5" />
            {note}
          </span>
        )}
      </div>
      {value === undefined && inheritedLabel && (
        <p className="text-xs text-gray-500">{inheritedLabel}</p>
      )}
      {swatches.length > 0 && (
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={`${label} suggestions`}>
          {swatches.map((s) => (
            <button
              key={s.color}
              type="button"
              disabled={disabled}
              onClick={() => onChange(s.color)}
              title={s.label ? `${s.label} ${s.color}` : s.color}
              aria-label={`Use ${s.label ?? s.color}`}
              aria-pressed={shown === s.color}
              className={`h-8 w-8 rounded-full border-2 shadow-sm ${
                shown === s.color ? 'border-primary-600 ring-2 ring-primary-200' : 'border-white'
              }`}
              style={{ backgroundColor: s.color }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
